/** Pure maths of the zoom pane: a scale plus a translation (CSS `translate(x, y) scale(s)`). */

export interface ZoomTransform {
  scale: number;
  /** Translation in viewport pixels (applied before the scale). */
  x: number;
  y: number;
}

export interface ZoomSize {
  width: number;
  height: number;
}

export const ZOOM_STEP = 1.2;
/** Smoothness of Ctrl+wheel zoom: the factor is exp(-deltaY * this). */
export const ZOOM_WHEEL_SENSITIVITY = 0.0015;

export function clampScale(scale: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, scale));
}

/**
 * The transform that shows the whole content in the viewport: scaled down to
 * fit (never above `maxFitScale`, never outside min/max), centred
 * horizontally, top-aligned. Content that does not fit even at the minimum
 * scale is aligned to the top-left corner. Unmeasured sizes give the
 * identity transform.
 */
export function fitTransform(
  viewport: ZoomSize,
  content: ZoomSize,
  options: {
    minScale: number;
    maxScale: number;
    padding?: number;
    maxFitScale?: number;
  }
): ZoomTransform {
  const padding = options.padding ?? 16;
  if (
    viewport.width <= 0 ||
    viewport.height <= 0 ||
    content.width <= 0 ||
    content.height <= 0
  ) {
    return { scale: 1, x: 0, y: 0 };
  }
  const availableWidth = Math.max(1, viewport.width - 2 * padding);
  const availableHeight = Math.max(1, viewport.height - 2 * padding);
  const raw = Math.min(
    availableWidth / content.width,
    availableHeight / content.height
  );
  const scale = clampScale(
    raw,
    options.minScale,
    Math.min(options.maxScale, options.maxFitScale ?? 1)
  );
  const scaledWidth = content.width * scale;
  const x =
    scaledWidth >= viewport.width - 2 * padding
      ? padding
      : (viewport.width - scaledWidth) / 2;
  return { scale, x, y: padding };
}

/**
 * Zooms by `factor` keeping the point (`cx`, `cy`), in viewport pixels,
 * fixed on screen. The scale is clamped; when it is already at the limit the
 * transform comes back unchanged.
 */
export function zoomAt(
  transform: ZoomTransform,
  factor: number,
  cx: number,
  cy: number,
  min: number,
  max: number
): ZoomTransform {
  const scale = clampScale(transform.scale * factor, min, max);
  if (scale === transform.scale) return transform;
  const ratio = scale / transform.scale;
  return {
    scale,
    x: cx - (cx - transform.x) * ratio,
    y: cy - (cy - transform.y) * ratio,
  };
}

/** The zoom factor of one wheel event (a negative delta zooms in). */
export function wheelZoomFactor(deltaY: number): number {
  return Math.exp(-deltaY * ZOOM_WHEEL_SENSITIVITY);
}

export function panBy(
  transform: ZoomTransform,
  dx: number,
  dy: number
): ZoomTransform {
  return { ...transform, x: transform.x + dx, y: transform.y + dy };
}

/** The CSS transform value of a transform. */
export function toCssTransform(transform: ZoomTransform): string {
  return `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`;
}
